// API service for Assessor applications management
// Handles all API calls related to assessor operations

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

class AssessorApiService {
  // Helper method to handle API responses
  static async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // Helper method to get auth headers
  static getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Validate user session
  static validateUserSession() {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return !!(userData && userData.orgId);
  }

  // Get assessor acceptance status and validation
  static async getAcceptanceStatus(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/acceptance/${orgId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getAcceptanceStatus error:', error);
      throw error;
    }
  }

  // Get certification files for assessor
  static async getCertificationFiles(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/certification-files/${orgId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getCertificationFiles error:', error);
      throw error;
    }
  }

  // Check assessor login status
  static async checkLogin(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/check-login/${orgId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.checkLogin error:', error);
      throw error;
    }
  }

  // Check SCF button visibility
  static async checkSCFVisibility(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/scf-visibility/${orgId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.checkSCFVisibility error:', error);
      throw error;
    }
  }

  // Get applications list for assessor with filters
  static async getApplicationsList({ 
    orgId, 
    appType = 'new', 
    labTypeId = 0, 
    searchText = '' 
  }) {
    try {
      const params = new URLSearchParams({
        appType,
        labTypeId: labTypeId.toString(),
        searchText
      });

      const response = await fetch(`${API_BASE_URL}/assessor/applications/${orgId}?${params}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getApplicationsList error:', error);
      throw error;
    }
  }

  // Reset assessor login
  static async resetLogin({ orgId, password }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/reset-login`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ orgId, password })
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.resetLogin error:', error);
      throw error;
    }
  }

  // Export applications to Excel
  static async exportToExcel({ 
    orgId, 
    appType = 'new', 
    labTypeId = 0, 
    searchText = '' 
  }) {
    try {
      const params = new URLSearchParams({
        appType,
        labTypeId: labTypeId.toString(),
        searchText
      });

      const response = await fetch(`${API_BASE_URL}/assessor/export-excel/${orgId}?${params}`, {
        method: 'GET',
        headers: {
          ...(localStorage.getItem('authToken') && { 
            'Authorization': `Bearer ${localStorage.getItem('authToken')}` 
          })
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Return blob for file download
      return response.blob();
    } catch (error) {
      console.error('AssessorApiService.exportToExcel error:', error);
      throw error;
    }
  }

  // Find organization ID by application ID
  static async findOrgIdByAppId(appId) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/find-orgid/${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.findOrgIdByAppId error:', error);
      throw error;
    }
  }

  // Get user type for assessor
  static async getUserType(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/user-type/${orgId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getUserType error:', error);
      throw error;
    }
  }

  // Get all lab types
  static async getLabTypes() {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/lab-types`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getLabTypes error:', error);
      throw error;
    }
  }

  // Check 5 year requirement
  static async check5YearRequirement(orgId) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/check-5year/${orgId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.check5YearRequirement error:', error);
      throw error;
    }
  }

  // Format date helper
  static formatDate(dateString) {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  }

  // Get application type display name
  static getAppTypeDisplayName(appType) {
    const types = {
      'new': 'New Applications',
      'mainAssessment': 'Main Assessment',
      'completed': 'Completed Applications'
    };
    return types[appType] || 'Applications';
  }

  // Download Excel file helper
  static downloadExcelFile(blob, filename = 'Assessor_Applications.xlsx') {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Accept application - update assessoralloted table and stage
  static async acceptApplication({ appId, orgId, assessorAllotedId, assessorId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/application/accept`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          appId,
          orgId,
          assessorAllotedId,
          assessorId
        })
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.acceptApplication error:', error);
      throw error;
    }
  }

  // Reject application - update assessoralloted table and stage
  static async rejectApplication({ appId, orgId, assessorAllotedId, assessorId, remarks }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/application/reject`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          appId,
          orgId,
          assessorAllotedId,
          assessorId,
          remarks
        })
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.rejectApplication error:', error);
      throw error;
    }
  }

  // Get detailed application information
  static async getApplicationDetails({ appId, orgId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/application/details/${appId}/${orgId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getApplicationDetails error:', error);
      throw error;
    }
  }

  // Get detailed laboratory information
  static async getLabDetails({ orgId, appId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/lab/details/${orgId}/${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getLabDetails error:', error);
      throw error;
    }
  }

  // Get disciplines for application
  static async getDisciplines({ orgId, appId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/application/disciplines/${orgId}/${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getDisciplines error:', error);
      throw error;
    }
  }

  // Get scopes for application
  static async getScopes({ orgId, appId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/application/scopes/${orgId}/${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getScopes error:', error);
      throw error;
    }
  }

  // Get equipment for application
  static async getEquipment({ orgId, appId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/application/equipment/${orgId}/${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getEquipment error:', error);
      throw error;
    }
  }

  // Get assessment slots for application
  static async getAssessmentSlots({ orgId, appId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/application/assessment-slots/${orgId}/${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getAssessmentSlots error:', error);
      throw error;
    }
  }

  // Get CAB details for application
  static async getCABDetails({ appId }) {
    try {
      const response = await fetch(`${API_BASE_URL}/assessor/application/cab-details/${appId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      return this.handleResponse(response);
    } catch (error) {
      console.error('AssessorApiService.getCABDetails error:', error);
      throw error;
    }
  }
}

export default AssessorApiService;
